/**
 * ExecutiveTrendChart — the report period's trend tile (mockup carbon/01
 * "Twelve weeks", blueprint §7 C28): one line per Guardian Score component
 * in its category mark, Secure Score dashed because it is Microsoft's, drawn
 * by the Carbon TrendChart from getChartTheme() (useChartTheme), so it
 * follows the theme toggle.
 *
 * The rows are the report's own `guardianScoreTrend`, ended on the tiles'
 * live values by the page (report-view reportTrendRows), and sampled the way
 * the Command Center samples its trend (cc-trend.ts pillarTrend): weekly once
 * there are eight or more days, daily before that, with a short-history note
 * for a new tenant. An empty history says so instead of drawing a flat line.
 */

import { useMemo } from 'react'
import clsx from 'clsx'
import { Tile, TrendChart } from '@/components/carbon'
import { pillarTrend, SHORT_HISTORY_DAYS, trendSubtitle } from '@/components/Dashboard/v2/cc-trend'
import type { ExecutiveReportData } from '@/types/executiveReport'

interface Props {
  data: ExecutiveReportData['guardianScoreTrend']
  /** Reporting window in days, named in the tile title. */
  periodDays: number
  className?: string
}

export default function ExecutiveTrendChart({ data, periodDays, className }: Props) {
  const trend = useMemo(() => pillarTrend(data), [data])
  const subtitle = trendSubtitle(trend)

  return (
    <Tile as="section" aria-labelledby="exec-trend-heading" className={clsx('min-w-0 pb-6', className)}>
      <h2 id="exec-trend-heading" className="m-0 text-heading font-normal text-text-primary">
        Last {periodDays} days
      </h2>
      {subtitle ? <p className="m-0 mt-0.5 text-helper text-text-helper">{subtitle}</p> : null}
      {trend.series.length === 0 ? (
        <p className="m-0 mt-4 border-t border-line-subtle py-6 text-helper text-text-helper">
          The trend fills in as Guardian records scores for this tenant.
        </p>
      ) : (
        <>
          {trend.days < SHORT_HISTORY_DAYS ? (
            <p className="m-0 mt-2 text-helper text-text-helper">
              This tenant has {trend.days} {trend.days === 1 ? 'day' : 'days'} of history so far.
            </p>
          ) : null}
          <TrendChart series={trend.series} xDomain={trend.xDomain} yDomain={trend.yDomain} className="mt-4" />
        </>
      )}
    </Tile>
  )
}
